import React from "react";
import {
  Box, Typography, Card, CardContent, Container, List, ListItem, ListItemIcon, ListItemText, Divider, useTheme, Paper, Button
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import UpdateIcon from "@mui/icons-material/Update";
import StorageIcon from "@mui/icons-material/Storage";
import CookieIcon from "@mui/icons-material/Cookie";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

export default function PrivacyPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const showBackToCookies = location.state && location.state.fromCookies;


  return (
    <Box sx={{
      bgcolor: theme.palette.mode === "dark"
        ? "linear-gradient(120deg, #181c21 0%, #232936 100%)"
        : "linear-gradient(120deg, #f8fafc 0%, #e0f7ef 100%)",
      minHeight: "100vh",
      py: { xs: 4, md: 7 }
    }}>
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            borderRadius: 5,
            p: { xs: 2.5, sm: 6 },
            boxShadow: "0 8px 32px 0 #49bc9025",
            background:
              theme.palette.mode === "dark"
                ? "rgba(30,36,46,0.92)"
                : "rgba(255,255,255,0.93)",
            color: theme.palette.text.primary,
            backdropFilter: "blur(2.5px)"
          }}
        >
          {/* ΝΕΟ: Κουμπί επιστροφής */}
          {showBackToCookies && (
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                sx={{ borderRadius: 2, fontWeight: 700, px: 3, mt: 1, mb: 2 }}
                onClick={() => {
                  // Επιστρέφει εκεί που ήταν, αλλά το cookie dialog θα εμφανιστεί πάλι
                  navigate(-1); // ή navigate("/", { replace: true })
                  setTimeout(() => {
                    localStorage.removeItem("cookieConsentAccepted");
                  }, 500); // έτσι το dialog θα εμφανιστεί πάλι
                }}
              >
                {t("back_to_cookie_consent") || "Επιστροφή στη σελίδα & Cookies"}
              </Button>
            </Box>
          )}
          {/* Τίτλος */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <SecurityIcon sx={{ color: theme.palette.primary.main, fontSize: 60, mb: 1 }} />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                letterSpacing: 1,
                mb: 1,
                color: "primary.main",
                textShadow: theme.palette.mode === "dark"
                  ? "0 2px 12px #0009"
                  : "0 2px 6px #49bc9040"
              }}>
              {t("privacy_policy.title")}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
              {t("privacy_policy.intro")}
            </Typography>
          </Box>
          {/* Sections */}
          <SectionCard
            icon={<StorageIcon color="primary" />}
            title={t("privacy_policy.data_collected_title")}
            items={t("privacy_policy.data_collected", { returnObjects: true })}
          />
          <SectionCard
            icon={<InfoOutlinedIcon color="secondary" />}
            title={t("privacy_policy.how_we_use_title")}
            items={t("privacy_policy.how_we_use", { returnObjects: true })}
          />
          <SectionCard
            icon={<GavelIcon color="action" />}
            title={t("privacy_policy.third_parties_title")}
            text={t("privacy_policy.third_parties")}
          />
          <SectionCard
            icon={<CookieIcon color="warning" />}
            title={t("privacy_policy.cookies_title")}
            text={t("privacy_policy.cookies")}
          />
          <SectionCard
            icon={<AccountCircleIcon color="info" />}
            title={t("privacy_policy.data_rights_title")}
            items={t("privacy_policy.data_rights", { returnObjects: true })}
          />
          <SectionCard
            icon={<EmailOutlinedIcon color="success" />}
            title={t("privacy_policy.dpo_title")}
            text={t("privacy_policy.dpo")}
          />
          <SectionCard
            icon={<UpdateIcon color="disabled" />}
            title={t("privacy_policy.policy_updates_title")}
            text={t("privacy_policy.policy_updates")}
            last={t("privacy_policy.last_updated")}
          />
          <SectionCard
            icon={<DeleteIcon color="error" />}
            title={t("privacy_policy.deletion_title")}
            items={t("privacy_policy.deletion_steps", { returnObjects: true })}
            text={t("privacy_policy.deletion_intro")}
          />
          <Divider sx={{ my: 4 }} />
          <Typography align="center" variant="caption" sx={{ color: "text.secondary" }}>
            © {new Date().getFullYear()} inCooking | {t("privacy_policy.title")}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

// Διορθωμένο SectionCard
function SectionCard({ icon, title, items, text, last }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        mb: 4,
        borderRadius: 4,
        background:
          theme.palette.mode === "dark"
            ? "rgba(25,35,54,0.92)"
            : "rgba(245, 250, 255, 0.83)",
        color: theme.palette.text.primary,
        boxShadow: "0 2px 14px #49bc9022",
        backdropFilter: "blur(1.5px)",
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box sx={{ mr: 1 }}>{icon}</Box>
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>
        </Box>
        {items && Array.isArray(items) && (
          <List dense sx={{ pl: 2 }}>
            {items.map((item, idx) => (
              <ListItem key={idx} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 28, color: "primary.light" }}>
                  <InfoOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        )}
        {text && (
          <Typography variant="body1" sx={{ color: "text.secondary", mb: last ? 1 : 0 }}>
            {text}
          </Typography>
        )}
        {last && (
          <Typography variant="caption" sx={{ color: "text.disabled", mt: 1 }}>
            {last}
          </Typography>
        )}
        
      </CardContent>
    </Card>
  );
}
